// Trial scorecard — one or more sense hubs (MCP tool polls), multi-select
// numeric fields as metrics. Host polls each hub once; metrics bind by senseId.
import { useClient } from '@cortex/react-query';
import type { MetricComparator, TrialScorecardFact } from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { PromoteKitButton } from './PromoteKitButton';
import { ScorecardChart } from './ScorecardChart';

interface IConn {
  id: string;
  name: string;
}

interface ITool {
  name: string;
  description?: string;
}

type BaselineMode = 'delta' | 'absolute';
type DriveOp = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'neq';
type MetricKind = 'outcome' | 'guardrail' | 'diagnostic';

interface ISenseDraft {
  key: string;
  /** Stable hub id stamped into metrics.senseId / drive.senseId */
  id: string;
  label: string;
  mcpConnectionId: string;
  toolName: string;
  pollSec: string;
  previewPaths: { path: string; value: number }[];
  selectedPaths: Record<string, boolean>;
  previewError: string | null;
  loadingFields: boolean;
}

interface IMetricDraft {
  key: string;
  senseKey: string;
  label: string;
  field: string;
  comparator: MetricComparator;
  target: string;
  baselineMode: BaselineMode;
  kind: MetricKind;
}

const draftStorageKey = (threadId: string) => `cortex.scorecardDraft.v2.${threadId}`;

interface IScorecardDraft {
  version: 2;
  objective: string;
  deadlineSec: string;
  senses: Array<{
    key: string;
    id: string;
    label: string;
    mcpConnectionId: string;
    toolName: string;
    pollSec: string;
  }>;
  metrics: IMetricDraft[];
  driveOn: boolean;
  driveSenseKey: string;
  driveField: string;
  driveOp: DriveOp;
  driveValue: string;
  editing?: boolean;
}

function emptySense(seed?: Partial<ISenseDraft>): ISenseDraft {
  const key = seed?.key ?? crypto.randomUUID();
  const n = Math.random().toString(36).slice(2, 6);
  return {
    key,
    id: seed?.id ?? `hub_${n}`,
    label: seed?.label ?? '',
    mcpConnectionId: seed?.mcpConnectionId ?? '',
    toolName: seed?.toolName ?? '',
    pollSec: seed?.pollSec ?? '10',
    previewPaths: seed?.previewPaths ?? [],
    selectedPaths: seed?.selectedPaths ?? {},
    previewError: seed?.previewError ?? null,
    loadingFields: false,
  };
}

function emptyMetric(seed?: Partial<IMetricDraft>): IMetricDraft {
  return {
    key: crypto.randomUUID(),
    senseKey: '',
    label: '',
    field: '',
    comparator: '>=',
    target: '0',
    baselineMode: 'delta',
    kind: 'outcome',
    ...seed,
  };
}

function slugId(s: string): string {
  return (
    s
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 40) || 'metric'
  );
}

function extractTools(raw: unknown): ITool[] {
  if (!raw || typeof raw !== 'object') return [];
  const o = raw as Record<string, unknown>;
  const list = Array.isArray(o.tools) ? o.tools : Array.isArray(raw) ? raw : [];
  const out: ITool[] = [];
  for (const t of list) {
    if (!t || typeof t !== 'object') continue;
    const name = (t as { name?: unknown }).name;
    if (typeof name !== 'string' || !name) continue;
    const description = (t as { description?: unknown }).description;
    const tool: ITool = { name };
    if (typeof description === 'string') tool.description = description;
    out.push(tool);
  }
  return out;
}

function unwrapToolPayload(raw: unknown): unknown {
  if (raw == null) return raw;
  if (typeof raw !== 'object') return raw;
  const o = raw as Record<string, unknown>;
  const content = o.content;
  if (Array.isArray(content)) {
    for (const part of content) {
      if (part && typeof part === 'object' && (part as { type?: string }).type === 'text') {
        const text = (part as { text?: unknown }).text;
        if (typeof text === 'string') {
          try {
            return JSON.parse(text) as unknown;
          } catch {
            return text;
          }
        }
      }
    }
  }
  if ('structuredContent' in o) return o.structuredContent;
  if ('result' in o) return unwrapToolPayload(o.result);
  return raw;
}

function collectNumericPaths(
  value: unknown,
  prefix = '',
  out: { path: string; value: number }[] = [],
) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (prefix) out.push({ path: prefix, value });
    return out;
  }
  if (typeof value === 'boolean') {
    if (prefix) out.push({ path: prefix, value: value ? 1 : 0 });
    return out;
  }
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    out.push({ path: prefix, value: Number(value) });
    return out;
  }
  if (Array.isArray(value)) {
    out.push({ path: prefix ? `${prefix}.length` : '$.length', value: value.length });
    return out;
  }
  if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const path = prefix ? `${prefix}.${k}` : k;
      collectNumericPaths(v, path, out);
    }
  }
  return out;
}

function readDraft(threadId: string): IScorecardDraft | null {
  try {
    // Prefer v2; migrate v1 single-tool drafts if present.
    const v2 = localStorage.getItem(draftStorageKey(threadId));
    if (v2) {
      const parsed: unknown = JSON.parse(v2);
      if (parsed && typeof parsed === 'object') {
        const d = parsed as Record<string, unknown>;
        if (d.version === 2) return normalizeDraft(d);
      }
    }
    const v1raw = localStorage.getItem(`cortex.scorecardDraft.v1.${threadId}`);
    if (!v1raw) return null;
    const v1: unknown = JSON.parse(v1raw);
    if (!v1 || typeof v1 !== 'object') return null;
    const o = v1 as Record<string, unknown>;
    const senseKey = crypto.randomUUID();
    const senseId = 'main';
    return normalizeDraft({
      version: 2,
      objective: o.objective,
      deadlineSec: o.deadlineSec,
      senses: [
        {
          key: senseKey,
          id: senseId,
          label: 'Main',
          mcpConnectionId: o.mcpConnectionId,
          toolName: o.toolName,
          pollSec: o.pollSec,
        },
      ],
      metrics: Array.isArray(o.metrics)
        ? o.metrics.map((rawM) => {
            const m = rawM && typeof rawM === 'object' ? (rawM as Record<string, unknown>) : {};
            return {
              key: typeof m.key === 'string' ? m.key : crypto.randomUUID(),
              senseKey,
              label: typeof m.label === 'string' ? m.label : '',
              field: typeof m.field === 'string' ? m.field : '',
              comparator: m.comparator,
              target: typeof m.target === 'string' ? m.target : '0',
              baselineMode: m.baselineMode,
              kind: m.kind,
            };
          })
        : [],
      driveOn: o.driveOn,
      driveSenseKey: senseKey,
      driveField: o.driveField,
      driveOp: o.driveOp,
      driveValue: o.driveValue,
      editing: o.editing,
    });
  } catch {
    return null;
  }
}

function normalizeDraft(d: Record<string, unknown>): IScorecardDraft {
  const sensesRaw = Array.isArray(d.senses) ? d.senses : [];
  const senses = sensesRaw.map((rawS, i) => {
    const s = rawS && typeof rawS === 'object' ? (rawS as Record<string, unknown>) : {};
    return {
      key: typeof s.key === 'string' ? s.key : crypto.randomUUID(),
      id: typeof s.id === 'string' && s.id ? s.id : `hub_${i + 1}`,
      label: typeof s.label === 'string' ? s.label : '',
      mcpConnectionId: typeof s.mcpConnectionId === 'string' ? s.mcpConnectionId : '',
      toolName: typeof s.toolName === 'string' ? s.toolName : '',
      pollSec: typeof s.pollSec === 'string' ? s.pollSec : '10',
    };
  });
  const metricsRaw = Array.isArray(d.metrics) ? d.metrics : [];
  const fallbackSenseKey = senses[0]?.key ?? '';
  return {
    version: 2,
    objective: typeof d.objective === 'string' ? d.objective : '',
    deadlineSec: typeof d.deadlineSec === 'string' ? d.deadlineSec : '1200',
    senses,
    metrics: metricsRaw.map((rawM) => {
      const m = rawM && typeof rawM === 'object' ? (rawM as Record<string, unknown>) : {};
      return emptyMetric({
        key: typeof m.key === 'string' ? m.key : crypto.randomUUID(),
        senseKey: typeof m.senseKey === 'string' ? m.senseKey : fallbackSenseKey,
        label: typeof m.label === 'string' ? m.label : '',
        field: typeof m.field === 'string' ? m.field : '',
        comparator:
          m.comparator === '>=' ||
          m.comparator === '<=' ||
          m.comparator === '>' ||
          m.comparator === '<' ||
          m.comparator === '=='
            ? m.comparator
            : '>=',
        target: typeof m.target === 'string' ? m.target : '0',
        baselineMode: m.baselineMode === 'absolute' ? 'absolute' : 'delta',
        kind: m.kind === 'guardrail' || m.kind === 'diagnostic' ? m.kind : 'outcome',
      });
    }),
    driveOn: Boolean(d.driveOn),
    driveSenseKey: typeof d.driveSenseKey === 'string' ? d.driveSenseKey : fallbackSenseKey,
    driveField: typeof d.driveField === 'string' ? d.driveField : '',
    driveOp:
      d.driveOp === 'gt' ||
      d.driveOp === 'gte' ||
      d.driveOp === 'lt' ||
      d.driveOp === 'lte' ||
      d.driveOp === 'eq' ||
      d.driveOp === 'neq'
        ? d.driveOp
        : 'gt',
    driveValue: typeof d.driveValue === 'string' ? d.driveValue : '0',
    editing: Boolean(d.editing),
  };
}

function writeDraft(
  threadId: string,
  draft: Omit<IScorecardDraft, 'version'> & { version?: 2 },
): void {
  try {
    const payload: IScorecardDraft = { version: 2, ...draft };
    localStorage.setItem(draftStorageKey(threadId), JSON.stringify(payload));
  } catch {
    /* private mode / quota */
  }
}

function clearDraft(threadId: string): void {
  try {
    localStorage.removeItem(draftStorageKey(threadId));
    localStorage.removeItem(`cortex.scorecardDraft.v1.${threadId}`);
  } catch {
    /* ignore */
  }
}

function draftFromBoard(board: TrialScorecardFact): {
  objective: string;
  deadlineSec: string;
  senses: ISenseDraft[];
  metrics: IMetricDraft[];
  driveOn: boolean;
  driveSenseKey: string;
  driveField: string;
  driveOp: DriveOp;
  driveValue: string;
} {
  const senses = board.senses.map((s, i) =>
    emptySense({
      id: s.id,
      label: s.id === 'main' ? 'Main' : s.id,
      mcpConnectionId: s.mcpConnectionId,
      toolName: s.toolName,
      pollSec: String(s.intervalSec ?? 10),
      key: `loaded-${s.id}-${i}`,
    }),
  );
  const bySenseId = new Map(senses.map((s) => [s.id, s.key]));
  const metrics = board.metrics.map((m) =>
    emptyMetric({
      senseKey: bySenseId.get(m.senseId) ?? senses[0]?.key ?? '',
      label: m.label ?? m.id,
      field: m.selector,
      comparator: m.comparator,
      target: String(m.target),
      baselineMode: m.baselineMode === 'absolute' ? 'absolute' : 'delta',
      kind: m.kind ?? 'outcome',
    }),
  );
  const driveSenseKey = board.drive
    ? (bySenseId.get(board.drive.senseId) ?? senses[0]?.key ?? '')
    : (senses[0]?.key ?? '');
  return {
    objective: board.objective ?? '',
    deadlineSec: board.deadlineAt
      ? String(Math.max(60, Math.round((new Date(board.deadlineAt).getTime() - Date.now()) / 1000)))
      : '1200',
    senses: senses.length ? senses : [emptySense({ id: 'main', label: 'Main' })],
    metrics,
    driveOn: Boolean(board.drive),
    driveSenseKey,
    driveField: board.drive?.selector ?? '',
    driveOp: board.drive?.op ?? 'gt',
    driveValue: board.drive != null ? String(board.drive.value) : '0',
  };
}

function HubToolsSelect({
  connectionId,
  toolName,
  onChange,
}: {
  connectionId: string;
  toolName: string;
  onChange: (tool: string) => void;
}) {
  const client = useClient();
  const toolsQuery = useQuery({
    queryKey: ['mcp-tools-scorecard', connectionId],
    enabled: Boolean(connectionId),
    queryFn: () => client.http.get(`v1/mcp-connections/${connectionId}/tools`).json<unknown>(),
  });
  const tools = useMemo(() => extractTools(toolsQuery.data), [toolsQuery.data]);

  return (
    <select
      className="select-native"
      value={toolName}
      disabled={!connectionId || toolsQuery.isLoading}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">
        {!connectionId
          ? '— connection first —'
          : toolsQuery.isLoading
            ? 'Loading tools…'
            : tools.length === 0
              ? '— no tools —'
              : '— select tool —'}
      </option>
      {tools.map((t) => (
        <option key={t.name} value={t.name} title={t.description}>
          {t.name}
        </option>
      ))}
    </select>
  );
}

export function TrialScorecardPanel({ agentId, threadId }: { agentId: string; threadId: string }) {
  const client = useClient();
  const qc = useQueryClient();
  const base = `v1/persistent-agent-groups/${agentId}/agents/${threadId}`;

  const board = useQuery({
    queryKey: ['pa-trial-scorecard', threadId],
    queryFn: () => client.http.get(`${base}/scorecard`).json<TrialScorecardFact | null>(),
    refetchInterval: (q) => (q.state.data?.status === 'active' ? 5_000 : false),
  });

  const connections = useQuery({
    queryKey: ['mcp-connections-scorecard'],
    queryFn: async () => {
      const res = await client.http.get('v1/mcp-connections').json<{ items?: IConn[] } | IConn[]>();
      return Array.isArray(res) ? res : (res.items ?? []);
    },
  });

  const initialDraft = useMemo(() => readDraft(threadId), [threadId]);
  const [editing, setEditing] = useState(() => Boolean(initialDraft?.editing));
  const [objective, setObjective] = useState(() => initialDraft?.objective ?? '');
  const [deadlineSec, setDeadlineSec] = useState(() => initialDraft?.deadlineSec ?? '1200');
  const [senses, setSenses] = useState<ISenseDraft[]>(() => {
    if (initialDraft?.senses?.length) {
      return initialDraft.senses.map((s) => emptySense(s));
    }
    return [emptySense({ id: 'main', label: 'Main' })];
  });
  const [metrics, setMetrics] = useState<IMetricDraft[]>(() => initialDraft?.metrics ?? []);
  const [driveOn, setDriveOn] = useState(() => initialDraft?.driveOn ?? false);
  const [driveSenseKey, setDriveSenseKey] = useState(
    () => initialDraft?.driveSenseKey ?? initialDraft?.senses?.[0]?.key ?? '',
  );
  const [driveField, setDriveField] = useState(() => initialDraft?.driveField ?? '');
  const [driveOp, setDriveOp] = useState<DriveOp>(() => initialDraft?.driveOp ?? 'gt');
  const [driveValue, setDriveValue] = useState(() => initialDraft?.driveValue ?? '0');
  const [draftBanner, setDraftBanner] = useState(() =>
    initialDraft && (initialDraft.metrics.length > 0 || initialDraft.senses.some((s) => s.toolName))
      ? 'Restored unsaved scorecard draft from this browser.'
      : null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    writeDraft(threadId, {
      objective,
      deadlineSec,
      senses: senses.map((s) => ({
        key: s.key,
        id: s.id,
        label: s.label,
        mcpConnectionId: s.mcpConnectionId,
        toolName: s.toolName,
        pollSec: s.pollSec,
      })),
      metrics,
      driveOn,
      driveSenseKey,
      driveField,
      driveOp,
      driveValue,
      editing,
    });
  }, [
    threadId,
    objective,
    deadlineSec,
    senses,
    metrics,
    driveOn,
    driveSenseKey,
    driveField,
    driveOp,
    driveValue,
    editing,
  ]);

  useEffect(() => {
    const d = readDraft(threadId);
    if (!d) {
      setEditing(false);
      setObjective('');
      setDeadlineSec('1200');
      setSenses([emptySense({ id: 'main', label: 'Main' })]);
      setMetrics([]);
      setDriveOn(false);
      setDriveSenseKey('');
      setDriveField('');
      setDriveOp('gt');
      setDriveValue('0');
      setDraftBanner(null);
      return;
    }
    setEditing(Boolean(d.editing));
    setObjective(d.objective);
    setDeadlineSec(d.deadlineSec);
    setSenses(
      d.senses.length
        ? d.senses.map((s) => emptySense(s))
        : [emptySense({ id: 'main', label: 'Main' })],
    );
    setMetrics(d.metrics);
    setDriveOn(d.driveOn);
    setDriveSenseKey(d.driveSenseKey);
    setDriveField(d.driveField);
    setDriveOp(d.driveOp);
    setDriveValue(d.driveValue);
    setDraftBanner(
      d.metrics.length > 0 || d.senses.some((s) => s.toolName)
        ? 'Restored unsaved scorecard draft from this browser.'
        : null,
    );
  }, [threadId]);

  useEffect(() => {
    if (!editing) return;
    if (metrics.length > 0 || senses.some((s) => s.toolName || s.mcpConnectionId)) return;
    if (board.data?.metrics?.length) {
      const d = draftFromBoard(board.data);
      setObjective(d.objective);
      setDeadlineSec(d.deadlineSec);
      setSenses(d.senses);
      setMetrics(d.metrics);
      setDriveOn(d.driveOn);
      setDriveSenseKey(d.driveSenseKey);
      setDriveField(d.driveField);
      setDriveOp(d.driveOp);
      setDriveValue(d.driveValue);
      setDraftBanner('Loaded from the active scorecard on this thread.');
    }
  }, [editing, board.data, metrics.length, senses]);

  const patchSense = (key: string, patch: Partial<ISenseDraft>) => {
    setSenses((list) => list.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  };

  const loadFieldsFor = async (senseKey: string) => {
    const sense = senses.find((s) => s.key === senseKey);
    if (!sense) return;
    if (!sense.mcpConnectionId || !sense.toolName) {
      patchSense(senseKey, { previewError: 'Pick connection and tool first' });
      return;
    }
    patchSense(senseKey, { loadingFields: true, previewError: null });
    try {
      const raw = await client.http
        .post(
          `v1/mcp-connections/${sense.mcpConnectionId}/tools/${encodeURIComponent(sense.toolName)}/call`,
          { json: { arguments: {} } },
        )
        .json<unknown>();
      const payload = unwrapToolPayload(raw);
      const paths = collectNumericPaths(payload).sort((a, b) => a.path.localeCompare(b.path));
      if (paths.length === 0) {
        throw new Error('No numeric fields in tool result.');
      }
      patchSense(senseKey, {
        previewPaths: paths,
        selectedPaths: {},
        loadingFields: false,
        previewError: null,
      });
    } catch (e) {
      patchSense(senseKey, {
        loadingFields: false,
        previewError: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const addSelectedFromSense = (senseKey: string) => {
    const sense = senses.find((s) => s.key === senseKey);
    if (!sense) return;
    const picked = sense.previewPaths.filter((p) => sense.selectedPaths[p.path]);
    if (picked.length === 0) {
      setError('Check one or more fields on that hub, then Add selected');
      return;
    }
    setMetrics((ms) => {
      const existing = new Set(ms.filter((m) => m.senseKey === senseKey).map((m) => m.field));
      const next = [...ms];
      for (const p of picked) {
        if (existing.has(p.path)) continue;
        next.push(
          emptyMetric({
            senseKey,
            field: p.path,
            label: p.path,
            target: String(p.value),
            baselineMode: 'delta',
            comparator: '>=',
          }),
        );
      }
      return next;
    });
    setError(null);
  };

  const removeSense = (key: string) => {
    if (senses.length <= 1) {
      setError('Need at least one tool hub');
      return;
    }
    setSenses((list) => list.filter((s) => s.key !== key));
    setMetrics((ms) => ms.filter((m) => m.senseKey !== key));
    if (driveSenseKey === key) {
      const rest = senses.filter((s) => s.key !== key);
      setDriveSenseKey(rest[0]?.key ?? '');
    }
  };

  const refresh = () => {
    fork(qc.invalidateQueries({ queryKey: ['pa-trial-scorecard', threadId] }));
    fork(qc.invalidateQueries({ queryKey: ['pa-scorecard-samples', threadId] }));
  };

  const put = useMutation({
    mutationFn: async () => {
      if (senses.length < 1) throw new Error('Add at least one tool hub');
      if (metrics.length < 1) {
        throw new Error('Add at least one metric (Load fields → check → Add selected)');
      }

      const sensePayload = senses.map((s, i) => {
        const connId = s.mcpConnectionId.trim();
        const tool = s.toolName.trim();
        if (!connId || !tool) throw new Error(`Hub ${i + 1}: pick connection and tool`);
        const id = (s.id.trim() || `hub_${i + 1}`).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
        return {
          draftKey: s.key,
          id,
          mcpConnectionId: connId,
          toolName: tool,
          args: {} as Record<string, unknown>,
          intervalSec: Math.min(Math.max(Number(s.pollSec) || 10, 2), 3600),
        };
      });

      // De-dupe same conn|tool|{} — API rejects duplicates.
      const seen = new Map<string, string>();
      for (const s of sensePayload) {
        const k = `${s.mcpConnectionId}|${s.toolName}|{}`;
        if (seen.has(k)) {
          throw new Error(
            `Hubs "${seen.get(k)}" and "${s.id}" poll the same tool — merge into one hub`,
          );
        }
        seen.set(k, s.id);
      }

      const idByDraftKey = new Map(sensePayload.map((s) => [s.draftKey, s.id]));

      const parsedMetrics = metrics.map((m, i) => {
        const field = m.field.trim();
        if (!field) throw new Error(`Metric ${i + 1}: field required`);
        const senseId = idByDraftKey.get(m.senseKey);
        if (!senseId) throw new Error(`Metric ${i + 1}: pick which hub/tool it reads`);
        const target = Number(m.target);
        if (!Number.isFinite(target)) throw new Error(`Metric ${i + 1}: target must be a number`);
        return {
          id: `${slugId(m.label || field)}_${i}`,
          label: (m.label || field).trim(),
          senseId,
          selector: field,
          aggregate: 'value' as const,
          comparator: m.comparator,
          target,
          baselineMode: m.baselineMode,
          kind: m.kind,
        };
      });

      let drive = null as null | Record<string, unknown>;
      if (driveOn) {
        const field = driveField.trim();
        if (!field) throw new Error('Drive on — pick a field');
        const senseId = idByDraftKey.get(driveSenseKey);
        if (!senseId) throw new Error('Drive on — pick which hub the field comes from');
        const valueNum = Number(driveValue);
        drive = {
          senseId,
          selector: field,
          op: driveOp,
          value: Number.isFinite(valueNum) ? valueNum : driveValue,
          wakeDelivery: 'when-idle',
          coalesceSec: 60,
          wakePrompt: `Keep working — ${field} still ${driveOp} ${driveValue}. Scorecard not finished.`,
        };
      }

      return client.http
        .put(`${base}/scorecard`, {
          json: {
            objective: objective.trim() || undefined,
            deadlineSeconds: Math.min(Math.max(Number(deadlineSec) || 1200, 60), 30 * 24 * 3600),
            senses: sensePayload.map(({ id, mcpConnectionId, toolName, args, intervalSec }) => ({
              id,
              mcpConnectionId,
              toolName,
              args,
              intervalSec,
            })),
            metrics: parsedMetrics,
            drive,
          },
        })
        .json<TrialScorecardFact>();
    },
    onSuccess: () => {
      clearDraft(threadId);
      setDraftBanner(null);
      setEditing(false);
      setError(null);
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const cancel = useMutation({
    mutationFn: () => client.http.delete(`${base}/scorecard`).json<TrialScorecardFact>(),
    onSuccess: () => refresh(),
  });

  const fact = board.data;
  const senseLabel = (key: string) => {
    const s = senses.find((x) => x.key === key);
    if (!s) return '—';
    return s.label || s.toolName || s.id || 'hub';
  };
  const configuredDriveFieldIsVisible =
    metrics.some(
      (metric) =>
        (!driveSenseKey || metric.senseKey === driveSenseKey) && metric.field === driveField,
    ) ||
    (senses.find((sense) => sense.key === driveSenseKey)?.previewPaths ?? []).some(
      (path) => path.path === driveField,
    );

  return (
    <section className="card" style={{ marginTop: 12 }}>
      <div
        className="card-header"
        style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}
      >
        <span className="card-title">Scorecard</span>
        {fact && (
          <span
            className={
              fact.status === 'completed'
                ? 'badge badge--success'
                : fact.status === 'active'
                  ? 'badge badge--info'
                  : 'badge'
            }
          >
            {fact.status}
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {!editing && (
            <>
              <PromoteKitButton agentId={agentId} threadId={threadId} />
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => setEditing(true)}
              >
                {fact?.status === 'active' ? 'Replace' : 'Set scorecard'}
              </button>
            </>
          )}
          {fact?.status === 'active' && (
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={cancel.isPending}
              onClick={() => cancel.mutate()}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
      <div className="card-body" style={{ paddingTop: 8 }}>
        {!fact && !editing && (
          <p className="t-sm t-muted" style={{ margin: 0 }}>
            Add one or more MCP tools as hubs, load fields, check what to grade. Multiple tools =
            multiple polls; metrics still share one scoreboard.
          </p>
        )}

        {fact && !editing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {fact.objective && <div className="t-sm">{fact.objective}</div>}
            <div className="t-xs t-muted">
              Hubs:{' '}
              {fact.senses.map((s) => (
                <code key={s.id} style={{ marginRight: 8 }}>
                  {s.id}:{s.toolName}
                </code>
              ))}
            </div>
            {fact.drive && (
              <div className="t-xs t-muted">
                Drive: while <code>{fact.drive.selector}</code> {fact.drive.op}{' '}
                {String(fact.drive.value)}
                {fact.drive.senseId ? (
                  <>
                    {' '}
                    on <code>{fact.drive.senseId}</code>
                  </>
                ) : null}
              </div>
            )}
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {fact.metrics.map((m) => (
                <li key={m.id} className="t-sm">
                  <span
                    className={
                      ['pass', 'achieved', 'healthy'].includes(m.lineStatus)
                        ? 'badge badge--success'
                        : ['fail', 'failed', 'violated'].includes(m.lineStatus)
                          ? 'badge badge--danger'
                          : 'badge'
                    }
                    style={{ marginRight: 6 }}
                  >
                    {m.lineStatus}
                  </span>
                  <strong>{m.label ?? m.id}</strong>
                  <span className="t-muted">
                    {' '}
                    · {m.senseId}/{m.selector}
                  </span>{' '}
                  = {m.lastValue ?? '—'}
                  {m.baselineMode === 'delta' && m.baselineValue != null
                    ? ` (need ${m.baselineValue + m.target})`
                    : ` vs ${m.comparator} ${m.target}`}
                </li>
              ))}
            </ul>
            <ScorecardChart agentId={agentId} threadId={threadId} board={fact} />
          </div>
        )}

        {editing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {draftBanner && (
              <p className="t-xs" style={{ margin: 0, color: 'var(--text-secondary)' }}>
                {draftBanner}{' '}
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  style={{ marginLeft: 6 }}
                  onClick={() => {
                    clearDraft(threadId);
                    setDraftBanner(null);
                    setObjective('');
                    setDeadlineSec('1200');
                    setSenses([emptySense({ id: 'main', label: 'Main' })]);
                    setMetrics([]);
                    setDriveOn(false);
                    setDriveSenseKey('');
                    setDriveField('');
                    setDriveOp('gt');
                    setDriveValue('0');
                  }}
                >
                  Discard draft
                </button>
              </p>
            )}

            <p className="t-xs t-muted" style={{ margin: 0 }}>
              <strong>Multi-tool:</strong> each hub is one MCP tool poll. Metrics pick which hub
              they read. Same tool twice is rejected — merge fields onto one hub.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 8 }}>
              <label className="field">
                <span className="field-label">Objective (optional)</span>
                <input
                  className="input"
                  value={objective}
                  placeholder="What should this trial achieve?"
                  onChange={(e) => setObjective(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field-label">Deadline (s)</span>
                <input
                  className="input t-mono"
                  value={deadlineSec}
                  onChange={(e) => setDeadlineSec(e.target.value)}
                />
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong className="t-sm">Tool hubs ({senses.length})</strong>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() =>
                  setSenses((list) => [
                    ...list,
                    emptySense({
                      id: `hub_${list.length + 1}`,
                      label: `Hub ${list.length + 1}`,
                    }),
                  ])
                }
              >
                + Add tool hub
              </button>
            </div>

            {senses.map((sense, si) => {
              const selectedCount = Object.values(sense.selectedPaths).filter(Boolean).length;
              return (
                <div
                  key={sense.key}
                  style={{
                    border: '1px solid var(--border, #333)',
                    borderRadius: 8,
                    padding: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <strong className="t-sm">
                      Hub {si + 1}
                      {sense.toolName ? ` · ${sense.toolName}` : ''}
                    </strong>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      disabled={senses.length <= 1}
                      onClick={() => removeSense(sense.key)}
                    >
                      Remove hub
                    </button>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr 90px',
                      gap: 8,
                    }}
                  >
                    <label className="field">
                      <span className="field-label">Label</span>
                      <input
                        className="input"
                        value={sense.label}
                        placeholder="e.g. Ops"
                        onChange={(e) => patchSense(sense.key, { label: e.target.value })}
                      />
                    </label>
                    <label className="field">
                      <span className="field-label">MCP connection</span>
                      <select
                        className="select-native"
                        value={sense.mcpConnectionId}
                        onChange={(e) =>
                          patchSense(sense.key, {
                            mcpConnectionId: e.target.value,
                            toolName: '',
                            previewPaths: [],
                            selectedPaths: {},
                            previewError: null,
                          })
                        }
                      >
                        <option value="">— select —</option>
                        {(connections.data ?? []).map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span className="field-label">Tool</span>
                      <HubToolsSelect
                        connectionId={sense.mcpConnectionId}
                        toolName={sense.toolName}
                        onChange={(tool) =>
                          patchSense(sense.key, {
                            toolName: tool,
                            previewPaths: [],
                            selectedPaths: {},
                            previewError: null,
                          })
                        }
                      />
                    </label>
                    <label className="field">
                      <span className="field-label">Poll (s)</span>
                      <input
                        className="input t-mono"
                        value={sense.pollSec}
                        onChange={(e) => patchSense(sense.key, { pollSec: e.target.value })}
                      />
                    </label>
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      disabled={!sense.mcpConnectionId || !sense.toolName || sense.loadingFields}
                      onClick={() => {
                        loadFieldsFor(sense.key).catch(() => undefined);
                      }}
                    >
                      {sense.loadingFields ? 'Loading…' : 'Load fields from tool'}
                    </button>
                    {sense.previewPaths.length > 0 && (
                      <button
                        type="button"
                        className="btn btn--primary btn--sm"
                        disabled={selectedCount === 0}
                        onClick={() => addSelectedFromSense(sense.key)}
                      >
                        Add selected as metrics ({selectedCount})
                      </button>
                    )}
                  </div>
                  {sense.previewError && (
                    <div className="alert alert--danger">{sense.previewError}</div>
                  )}
                  {sense.previewPaths.length > 0 && (
                    <div
                      style={{
                        border: '1px solid var(--border, #333)',
                        borderRadius: 6,
                        padding: 8,
                        maxHeight: 180,
                        overflow: 'auto',
                      }}
                    >
                      <p className="t-xs t-muted" style={{ margin: '0 0 6px' }}>
                        Check numbers from <code>{sense.toolName}</code> to grade.
                      </p>
                      {sense.previewPaths.map((p) => (
                        <label
                          key={p.path}
                          style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'center',
                            padding: '2px 0',
                            fontFamily: 'var(--font-mono, monospace)',
                            fontSize: 12,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={Boolean(sense.selectedPaths[p.path])}
                            onChange={(e) =>
                              patchSense(sense.key, {
                                selectedPaths: {
                                  ...sense.selectedPaths,
                                  [p.path]: e.target.checked,
                                },
                              })
                            }
                          />
                          <span style={{ flex: 1 }}>{p.path}</span>
                          <span className="t-muted">{p.value}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 6,
                }}
              >
                <strong className="t-sm">Metrics ({metrics.length})</strong>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() =>
                    setMetrics((ms) => [...ms, emptyMetric({ senseKey: senses[0]?.key ?? '' })])
                  }
                >
                  Add blank row
                </button>
              </div>
              {metrics.length === 0 && (
                <p className="t-xs t-muted">
                  No metrics yet — load fields on a hub and check the ones to grade.
                </p>
              )}
              {metrics.map((m) => (
                <div
                  key={m.key}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    marginBottom: 10,
                    alignItems: 'end',
                    padding: 8,
                    border: '1px solid var(--border, #333)',
                    borderRadius: 6,
                  }}
                >
                  <label className="field" style={{ flex: '1 1 140px', minWidth: 120 }}>
                    <span className="field-label">Hub</span>
                    <select
                      className="select-native"
                      value={m.senseKey}
                      onChange={(e) =>
                        setMetrics((ms) =>
                          ms.map((x) => (x.key === m.key ? { ...x, senseKey: e.target.value } : x)),
                        )
                      }
                    >
                      <option value="">—</option>
                      {senses.map((s) => (
                        <option key={s.key} value={s.key}>
                          {senseLabel(s.key)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field" style={{ flex: '1 1 140px', minWidth: 120 }}>
                    <span className="field-label">Kind</span>
                    <select
                      className="select-native"
                      value={m.kind}
                      onChange={(e) => {
                        const kind = e.target.value as MetricKind;
                        setMetrics((ms) =>
                          ms.map((x) =>
                            x.key === m.key
                              ? {
                                  ...x,
                                  kind,
                                  baselineMode:
                                    kind !== 'outcome' && x.kind === 'outcome'
                                      ? 'absolute'
                                      : x.baselineMode,
                                }
                              : x,
                          ),
                        );
                      }}
                    >
                      <option value="outcome">Outcome</option>
                      <option value="guardrail">Guardrail</option>
                      <option value="diagnostic">Diagnostic</option>
                    </select>
                  </label>
                  <label className="field" style={{ flex: '2 1 220px', minWidth: 180 }}>
                    <span className="field-label">Label</span>
                    <input
                      className="input"
                      title={m.label}
                      value={m.label}
                      onChange={(e) =>
                        setMetrics((ms) =>
                          ms.map((x) => (x.key === m.key ? { ...x, label: e.target.value } : x)),
                        )
                      }
                    />
                  </label>
                  <label className="field" style={{ flex: '2 1 260px', minWidth: 220 }}>
                    <span className="field-label">Field</span>
                    <input
                      className="input t-mono"
                      title={m.field}
                      value={m.field}
                      onChange={(e) =>
                        setMetrics((ms) =>
                          ms.map((x) => (x.key === m.key ? { ...x, field: e.target.value } : x)),
                        )
                      }
                    />
                  </label>
                  <label className="field" style={{ flex: '0 0 80px' }}>
                    <span className="field-label">Op</span>
                    <select
                      className="select-native"
                      value={m.comparator}
                      onChange={(e) =>
                        setMetrics((ms) =>
                          ms.map((x) =>
                            x.key === m.key
                              ? { ...x, comparator: e.target.value as MetricComparator }
                              : x,
                          ),
                        )
                      }
                    >
                      {(['>=', '<=', '>', '<', '=='] as const).map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field" style={{ flex: '0 0 100px' }}>
                    <span className="field-label">Target</span>
                    <input
                      className="input t-mono"
                      value={m.target}
                      onChange={(e) =>
                        setMetrics((ms) =>
                          ms.map((x) => (x.key === m.key ? { ...x, target: e.target.value } : x)),
                        )
                      }
                    />
                  </label>
                  <label className="field" style={{ flex: '1 1 160px', minWidth: 150 }}>
                    <span className="field-label">Baseline</span>
                    <select
                      className="select-native"
                      value={m.baselineMode}
                      onChange={(e) =>
                        setMetrics((ms) =>
                          ms.map((x) =>
                            x.key === m.key
                              ? { ...x, baselineMode: e.target.value as BaselineMode }
                              : x,
                          ),
                        )
                      }
                    >
                      <option value="delta">+N since start</option>
                      <option value="absolute">hit absolute N</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    style={{ flex: '0 0 auto' }}
                    onClick={() => setMetrics((ms) => ms.filter((x) => x.key !== m.key))}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <div style={{ borderTop: '1px solid var(--border, #333)', paddingTop: 8 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={driveOn}
                  onChange={(e) => setDriveOn(e.target.checked)}
                />
                <span className="t-sm">Drive — wake when idle while condition holds</span>
              </label>
              {driveOn && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    marginTop: 6,
                    alignItems: 'end',
                  }}
                >
                  <label className="field" style={{ flex: '1 1 160px', minWidth: 140 }}>
                    <span className="field-label">Hub</span>
                    <select
                      className="select-native"
                      value={driveSenseKey}
                      onChange={(e) => setDriveSenseKey(e.target.value)}
                    >
                      {senses.map((s) => (
                        <option key={s.key} value={s.key}>
                          {senseLabel(s.key)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field" style={{ flex: '2 1 260px', minWidth: 220 }}>
                    <span className="field-label">Field</span>
                    <select
                      className="select-native"
                      value={driveField}
                      onChange={(e) => setDriveField(e.target.value)}
                    >
                      <option value="">— pick —</option>
                      {driveField && !configuredDriveFieldIsVisible && (
                        <option value={driveField}>{driveField} (configured)</option>
                      )}
                      {metrics
                        .filter((m) => !driveSenseKey || m.senseKey === driveSenseKey)
                        .map((m) =>
                          m.field ? (
                            <option key={`m-${m.key}`} value={m.field}>
                              {m.field}
                            </option>
                          ) : null,
                        )}
                      {(senses.find((s) => s.key === driveSenseKey)?.previewPaths ?? []).map(
                        (p) => (
                          <option key={p.path} value={p.path}>
                            {p.path} ({p.value})
                          </option>
                        ),
                      )}
                    </select>
                  </label>
                  <label className="field" style={{ flex: '0 0 90px' }}>
                    <span className="field-label">Op</span>
                    <select
                      className="select-native"
                      value={driveOp}
                      onChange={(e) => setDriveOp(e.target.value as DriveOp)}
                    >
                      {(['gt', 'gte', 'lt', 'lte', 'eq', 'neq'] as const).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field" style={{ flex: '0 0 110px' }}>
                    <span className="field-label">Value</span>
                    <input
                      className="input t-mono"
                      value={driveValue}
                      onChange={(e) => setDriveValue(e.target.value)}
                    />
                  </label>
                  <span className="t-xs t-muted" style={{ alignSelf: 'end', paddingBottom: 8 }}>
                    ≤1 wake / 60s if idle
                  </span>
                </div>
              )}
            </div>

            {error && <div className="alert alert--danger">{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={put.isPending}
                onClick={() => put.mutate()}
              >
                Save scorecard
              </button>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => {
                  setEditing(false);
                  setError(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
